import { useEffect, useCallback, useRef } from "react";
import { Form } from "antd";
import { FormInstance } from "antd/lib/form";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { useGetWorkerLoggedIn } from "@lhd/hooks";
import { APP_TYPES } from "../constants";
import dayjs from "dayjs";

interface IProps {
  form: FormInstance;
  appPartsFlat: AppPartType[];
  appType?: string;
  recordId?: string | number;
  isCreate?: boolean;
}

export const BusinessTripReportsCalculator = ({
  form,
  appPartsFlat,
  appType,
  recordId,
  isCreate,
}: IProps) => {
  const workerLoggedIn = useGetWorkerLoggedIn();

  const findIdByFieldName = (name: string) => {
    const part = appPartsFlat.find((p) => p.name === name);
    return part ? String(part.id) : undefined;
  };

  const idKoutsuu = findIdByFieldName("交通移動費");
  const idShukuhaku = findIdByFieldName("宿泊費");
  const idNichitau = findIdByFieldName("日当");
  const idShitaku = findIdByFieldName("支度費");
  const idJippi = findIdByFieldName("実費弁償");
  const idSougaku = findIdByFieldName("精算申請総額");
  const idApplicant = findIdByFieldName("出張旅費申請者");
  const idEndDate = findIdByFieldName("終了日");
  const idSettlementDate = findIdByFieldName("精算対象月月");
  const idDepartment = findIdByFieldName("部門選択");

  const getPath = useCallback(
    (fieldId: string | undefined) => {
      if (!fieldId) return [];
      return recordId !== undefined ? [String(recordId), fieldId] : [fieldId];
    },
    [recordId],
  );

  const valKoutsuu = Form.useWatch(getPath(idKoutsuu), form);
  const valShukuhaku = Form.useWatch(getPath(idShukuhaku), form);
  const valNichitau = Form.useWatch(getPath(idNichitau), form);
  const valShitaku = Form.useWatch(getPath(idShitaku), form);
  const valJippi = Form.useWatch(getPath(idJippi), form);
  const valApplicant = Form.useWatch(getPath(idApplicant), form);
  const valEndDate = Form.useWatch(getPath(idEndDate), form);

  const prevApplicantRef = useRef<any>(valApplicant);

  useEffect(() => {
    if (
      appType !== APP_TYPES.BUSINESS_TRIP_REPORTS ||
      !idApplicant ||
      !idDepartment
    ) {
      return;
    }
    if (
      prevApplicantRef.current !== undefined &&
      prevApplicantRef.current !== valApplicant
    ) {
      form.setFieldValue(getPath(idDepartment), undefined);
    }
    prevApplicantRef.current = valApplicant;
  }, [valApplicant, idApplicant, idDepartment, appType, getPath, form]);

  useEffect(() => {
    if (appType !== APP_TYPES.BUSINESS_TRIP_REPORTS || !idApplicant) {
      return;
    }
    const isCreateMode = isCreate || recordId === "new";
    if (
      isCreateMode &&
      workerLoggedIn?.id &&
      (valApplicant === undefined ||
        valApplicant === null ||
        valApplicant === "")
    ) {
      form.setFieldValue(getPath(idApplicant), Number(workerLoggedIn.id));
    }
  }, [
    appType,
    idApplicant,
    workerLoggedIn,
    valApplicant,
    form,
    recordId,
    isCreate,
    getPath,
  ]);

  useEffect(() => {
    if (appType !== APP_TYPES.BUSINESS_TRIP_REPORTS || !idSettlementDate) {
      return;
    }
    const pathSettlement = getPath(idSettlementDate);
    if (!valEndDate) {
      form.setFieldValue(pathSettlement, "");
      return;
    }

    const date = dayjs(valEndDate);
    if (date.isValid()) {
      const formatted = date.startOf("month").format("YYYY-MM-DD 00:00:00");
      form.setFieldValue(pathSettlement, formatted);
    } else {
      form.setFieldValue(pathSettlement, "");
    }
  }, [valEndDate, idSettlementDate, form, appType, getPath]);

  useEffect(() => {
    if (appType !== APP_TYPES.BUSINESS_TRIP_REPORTS || !idSougaku) {
      return;
    }

    const parseNumber = (val: any) => {
      if (val === undefined || val === null || val === "") return 0;
      const cleanVal = String(val).replaceAll(",", "").trim();
      const num = Number(cleanVal);
      return isNaN(num) ? 0 : num;
    };

    const koutsuu = parseNumber(valKoutsuu);
    const shukuhaku = parseNumber(valShukuhaku);
    const nichitau = parseNumber(valNichitau);
    const shitaku = parseNumber(valShitaku);
    const jippi = parseNumber(valJippi);

    const total = koutsuu + shukuhaku + nichitau + shitaku - jippi;
    const formattedTotal = total.toLocaleString("en-US");

    const pathSougaku = getPath(idSougaku);
    if (pathSougaku.length > 0) {
      const currentSougaku = form.getFieldValue(pathSougaku);
      if (String(currentSougaku || "").replaceAll(",", "") !== String(total)) {
        form.setFieldValue(pathSougaku, total === 0 ? "" : formattedTotal);
      }
    }
  }, [
    valKoutsuu,
    valShukuhaku,
    valNichitau,
    valShitaku,
    valJippi,
    idSougaku,
    form,
    appType,
    recordId,
    getPath,
  ]);

  return null;
};

export default BusinessTripReportsCalculator;
